import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@finster/ui";
import { Check, Minus } from "lucide-react";

const MAX_STAGGER_INDEX = 12;

export function staggerStyle(index: number): React.CSSProperties {
  return {
    "--sl-stagger-index": Math.min(index, MAX_STAGGER_INDEX),
  } as React.CSSProperties;
}

export function AnimatedSpinner({
  className,
}: {
  className?: string;
}) {
  return (
    <span
      className={cn(
        "sl-spinner block h-5 w-5 rounded-full",
        className
      )}
      aria-hidden="true"
    />
  );
}

export function ActionSnackbar({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss: () => void;
}) {
  return (
    <div className="pointer-events-none fixed bottom-6 left-1/2 z-[70] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2">
      <div
        className="sl-animate-fade-in-up pointer-events-auto mx-auto flex min-w-72 items-center gap-3 rounded-full border border-border bg-background px-4 py-3 text-sm text-foreground shadow-xl"
        role="status"
        aria-live="polite"
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
          <Check className="h-3.5 w-3.5 stroke-[3]" />
        </span>
        <span className="min-w-0 flex-1 truncate">{message}</span>
        <button
          type="button"
          className="shrink-0 cursor-pointer rounded-full px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          onClick={onDismiss}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

interface FadeInThumbnailProps {
  src: string;
  alt: string;
  className?: string;
}

const loadedThumbnailSrcs = new Set<string>();

export const FadeInThumbnail = React.memo(function FadeInThumbnail({
  src,
  alt,
  className,
}: FadeInThumbnailProps) {
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const [loaded, setLoaded] = React.useState(() => loadedThumbnailSrcs.has(src));
  const [failed, setFailed] = React.useState(false);

  const [previousSrc, setPreviousSrc] = React.useState(src);
  if (previousSrc !== src) {
    setPreviousSrc(src);
    setLoaded(loadedThumbnailSrcs.has(src));
    setFailed(false);
  }
  React.useEffect(() => {
    const image = imageRef.current;
    if (!image) return;
    if (image.complete && image.naturalWidth > 0) {
      loadedThumbnailSrcs.add(src);
      setLoaded(true);
    } else if (image.complete && image.naturalWidth === 0) {
      setFailed(true);
    }
  }, [src]);

  return (
    <div className="relative h-full w-full">
      {!failed ? (
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => {
            loadedThumbnailSrcs.add(src);
            setLoaded(true);
          }}
          onError={() => setFailed(true)}
          data-loaded={loaded ? "true" : "false"}
          className={cn(
            "h-full w-full object-contain opacity-0 transition-opacity duration-150 ease-out",
            "data-[loaded=true]:opacity-100 motion-reduce:transition-none",
            className
          )}
        />
      ) : null}
      {(!loaded || failed) ? (
        <div className="absolute inset-0">
          <ProcessingThumbnail label="" />
        </div>
      ) : null}
    </div>
  );
});

export function ThumbnailPlaceholder({
  label,
  isError = false,
}: {
  label?: string;
  isError?: boolean;
}) {
  if (isError) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <span className="text-xs">{label || "Unavailable"}</span>
      </div>
    );
  }

  return (
    <div className="relative flex h-full items-center justify-center">
      <div className="absolute inset-0 animate-pulse bg-muted motion-reduce:animate-none" aria-hidden="true" />
      <AnimatedSpinner className="relative" />
    </div>
  );
}

export function ProcessingThumbnail({ label = "Processing" }: { label?: string }) {
  return (
    <div
      className="relative flex h-full flex-col items-center justify-center gap-2 text-muted-foreground"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="absolute inset-0 animate-pulse bg-muted motion-reduce:animate-none" aria-hidden="true" />
      <AnimatedSpinner className="relative z-10 h-6 w-6" />
      {label ? <span className="sl-processing-label relative text-xs font-medium">{label}</span> : null}
    </div>
  );
}

export function SelectionCheck({
  selected,
  indeterminate = false,
  className,
}: {
  selected: boolean;
  indeterminate?: boolean;
  className?: string;
}) {
  const isActive = selected || indeterminate;

  return (
    <span
      className={cn(
        "sl-check-pop flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[4px] border",
        isActive
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background/80",
        className
      )}
      data-selected={selected ? "true" : indeterminate ? "indeterminate" : "false"}
      aria-hidden="true"
    >
      {selected ? (
        <Check className="h-2.5 w-2.5 stroke-[3]" />
      ) : indeterminate ? (
        <Minus className="h-2.5 w-2.5 stroke-[3]" />
      ) : null}
    </span>
  );
}

export function AnimatedBackdrop({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className={cn("sl-portal-root sl-animate-backdrop fixed inset-0 z-[80]", className)} {...props}>
      {children}
    </div>,
    document.body
  );
}

export function AnimatedDialogPanel({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "sl-animate-scale-in w-full rounded-xl border border-border bg-background p-5 shadow-xl",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function AnimatedPopover({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "sl-popover-surface sl-animate-scale-in relative isolate z-50 overflow-hidden rounded-xl border border-border bg-background shadow-xl",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function AnimatedView({
  viewKey,
  className,
  children,
}: {
  viewKey: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div key={viewKey} className={cn("sl-view-enter", className)}>
      {children}
    </div>
  );
}
